import {
  confirmDialog,
  CustomButton,
  CustomCollapseCard,
  CustomTable,
  successDialog,
  Text,
  useText,
} from '@src/components';
import React, { useCallback, useEffect, useState } from 'react';
import '@src/styles/pendingApproval/balanceOrder.scss';
import { DownCircleOutlined, UpCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCardOrdersForStatusWithUpdaterUserName,
  pendingCardOrderDelete,
} from '@src/store/slice/cardOrderSlice';
import dayjs from '@src/utils/day';
import OrderSummaryCardModal from '../ticketRestaurant/myOrders/OrderSummaryCardModal';
import { setSelectedOrder } from '@src/store/slice/orderSlice';
import { useHistory } from 'react-router-dom';
import { moneyFormat } from '@src/utils/utils';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { getPendingNotifications } from '@src/store/slice/notificationSlice';
import PropTypes from 'prop-types';

function getExpandIcon() {
  return ({ expanded, onExpand, record }) =>
    expanded ? (
      <UpCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    ) : (
      <DownCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    );
}

function getExpandedRowRender(handleCancelOrder, handleApprovedOrder) {
  return (record) => (
    <div className="custom-expanded-row" key={record.id}>
      <div className="colum-title">
        <span>
          <Text t="transactionDate" />
        </span>{' '}
        <span>{dayjs(record.updateTime).format('DD/MM/YYYY HH:mm')}</span>
      </div>
      <div className="colum-title">
        <span>
          <Text t="numberOfCards" />
        </span>{' '}
        <span>{record.totalCount}</span>
      </div>
      <div className="colum-title">
        <span>
          <Text t="orderPrice" />:
        </span>{' '}
        <span>{record.totalPrice ? `${moneyFormat(record.totalPrice, false)} TL` : '0'}</span>
      </div>

      <div className="mobile-action-btn">
        <CustomButton
          id="pendingApproval-cardOrderList-custom-btn-cancel"
          onClick={() => handleCancelOrder(record)}
          className="custom-btn-cancel"
          buttonAuthName="AwaitingApprovalCancel"
        >
          <Text t="cancel2" />
        </CustomButton>
        <CustomButton
          id="pendingApproval-cardOrderList-custom-btn-approved"
          onClick={() => handleApprovedOrder(record)}
          className="custom-btn-approved"
          buttonAuthName="ApprovalAuthorizationGroup"
        >
          <Text t="approve" />
        </CustomButton>
      </div>
    </div>
  );
}

const DEFAULT_TABLE_PROPERTY = {
  currentPage: 1,
  page: 1,
  pageSize: 10,
  totalCount: 0,
};

const CardOrderList = ({ isMarket = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isMobil = useMediaQuery({ query: '(max-width: 1179.98px)' });

  const [cardOrderList, setCardOrderList] = useState([]);
  const [draftedTableProperty, setDraftedTableProperty] = useState(DEFAULT_TABLE_PROPERTY);
  const { processType } = useSelector((state) => state?.packages);
  const { selectedCustomer } = useSelector((state) => state?.customer);

  const [expandedRows, setExpandedRows] = useState([]);
  const [orderSummaryCardModalVisible, setOrderSummaryCardModalVisible] = useState(false);

  const attentionText = useText('attention');
  const cancelConfirmationText = useText('cancelConfirmation');
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');

  useEffect(() => {
    if (processType === 2 || processType === 0) loadCardOrders();
  }, [processType]);

  useEffect(() => {
    getList();
  }, []);

  const getList = async (pageNumber = 1, pageSize = 10) => {
    const body2 = {
      customerId: selectedCustomer?.customerId,
      orderStatus: 15,
      pageNumber,
      pageSize,
      vouId: isMarket ? 11 : 1,
    };
    const action = await dispatch(getCardOrdersForStatusWithUpdaterUserName(body2));
    if (getCardOrdersForStatusWithUpdaterUserName.fulfilled.match(action)) {
      setCardOrderList(action?.payload?.data?.items || []);
      setDraftedTableProperty(action?.payload?.data?.pagedProperty || DEFAULT_TABLE_PROPERTY);
    } else {
      setCardOrderList([]);
      setDraftedTableProperty(DEFAULT_TABLE_PROPERTY);
    }
  };

  const loadCardOrders = useCallback(() => {
    getList();
  }, [dispatch, selectedCustomer]);

  const handleCardTableChange = useCallback(
    (pageNumber, pageSize) => {
      getList(pageNumber, pageSize);
    },
    [dispatch, selectedCustomer, isMarket],
  );

  const handleCardDetail = (record) => {
    dispatch(setSelectedOrder(record));
    setOrderSummaryCardModalVisible(true);
  };

  const handleApprovedOrder = async (item) => {
    const invoiceMonth = item.invoiceMonth;
    const cardType = item.cardType;
    const cardActivationDate = item.cardActivationDate;
    let orderStatus = 0;
    if (invoiceMonth === dayjs().month() + 1) {
      orderStatus = 10;
    }
    dispatch(getPendingNotifications({}));
    history.push({
      pathname: '/ticket-restaurant/card-orders',
      state: {
        pendingApprovalId: item?.id,
        orderStatus,
        invoiceMonth,
        cardType,
        cardActivationDate,
      },
    });
  };

  const handleCancelOrder = async (id) => {
    const body = { id, CancelledByUser: true };

    confirmDialog({
      title: <Text t={attentionText} />,
      message: cancelConfirmationText ,
      onOk: async () => {
        const action = await dispatch(pendingCardOrderDelete(body));
        if (pendingCardOrderDelete.fulfilled.match(action)) {
          await getList();
          successDialog({
            title: <Text t="successfullySent" />,
            message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
            onOk: async () => {
              await loadCardOrders();
            },
            onCancel: () => { },
          });
        }
      },
    });
  };

  const columnsCard = [
    {
      title: <Text t="orderNoUpperCase" />,
      dataIndex: 'id',
      key: 'id',
      width: '20%',
      render: (text, record) => {
        return (
          <div className="balance-order-list-order-number" onClick={() => handleCardDetail(record)}>
            {text}
          </div>
        );
      },
    },
    {
      title: <Text t="createdBy" />,
      dataIndex: 'userFullName',
      key: 'userFullName',
    },
    {
      title: <Text t="orderDate" />,
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: '20%',
      responsive: ['md'],
      render: (text) => {
        return (
          <div>
            <div>{dayjs(text).format('DD/MM/YYYY HH:mm')}</div>
          </div>
        );
      },
    },
    {
      title: <Text t="totalCardsUpperCase" />,
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: '20%',
      responsive: ['md'],
    },
    {
      title: <Text t="orderPrice" />,
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: '20%',
      responsive: ['md'],
      render: (text) => <span>{text ? `${moneyFormat(text, false)} TL` : '0'}</span>,
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      responsive: ['md'],
      render: (_, record) => {
        return (
          <div className="row-btn-actions">
            <CustomButton
              id="pendingApproval-cardOrderList-custom-second-btn-cancel"
              onClick={() => handleCancelOrder(record?.id)}
              className="custom-btn-cancel"
              buttonAuthName="AwaitingApprovalCancel"
            >
              <Text t="cancel2" />
            </CustomButton>
            <CustomButton
              id="pendingApproval-cardOrderList-custom-second-btn-approved"
              onClick={() => handleApprovedOrder(record)}
              className="custom-btn-approved"
              buttonAuthName="ApprovalAuthorizationGroup"
            >
              <Text t="approve" />
            </CustomButton>
          </div>
        );
      },
    },
  ];

  return (
    <CustomCollapseCard
      className="card-order-card"
      cardTitle={<Text t="newCardOrderConfirmation" />}
    >
      <CustomTable
        pagination={
          draftedTableProperty?.totalCount > 10
            ? {
              total: draftedTableProperty?.totalCount,
              pageSize: draftedTableProperty?.pageSize,
              current: draftedTableProperty?.currentPage,
              onChange: (pageNumber, pageSize) => handleCardTableChange(pageNumber, pageSize),
              defaultCurrent: 1,
            }
            : false
        }
        isScroll={false}
        dataSource={cardOrderList}
        columns={columnsCard}
        rowKey={(record) => `list-${record?.id}`}
        expandedRowKeys={expandedRows}
        expandable={{
          showExpandColumn: false,
          expandIconColumnIndex: 7,
          onExpand: (expanded, record, key) =>
            expandedRows === key ? setExpandedRows(null) : setExpandedRows(key),
          expandIcon: isMobil ? getExpandIcon() : null,
          expandedRowRender: isMobil
            ? getExpandedRowRender(handleCancelOrder, handleApprovedOrder)
            : null,
        }}
      />
      <OrderSummaryCardModal
        modalVisible={orderSummaryCardModalVisible}
        handleModalVisible={setOrderSummaryCardModalVisible}
        isPending={true}
      />
    </CustomCollapseCard>
  );
};

CardOrderList.propTypes = {
  isMarket: PropTypes.bool,
};

export default CardOrderList;
